import { Box, FormControl, Grid, Paper, Tooltip, WithStyles } from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import * as React from "react";
import Typography from "@material-ui/core/Typography/Typography";
import { useTranslation } from "react-i18next";
import { styles } from "../ProductDetailsHistoricalBox.styles";
import { BlueButton } from "../../../components/Buttons/BlueButton";
import { FormDateField } from "../../../components/FormComponents/FormDateField";
import { KaikoDetailsComponent } from "../../../components/OrderDetails/KaikoDetailsComponent";
import { KaikoPricingInfoDialog } from "./KaikoPricingInfoDialog";
import { useEffect, useState } from "react";
import {
    getBaseCurrencies,
    getExchanges,
    getKaikoFilesInfo,
    getPricingPackages
} from "../../../../stores/filterableProducts/kaiko/KaikoAction";
import { useDispatch, useSelector } from "react-redux";
import {
    convertDateStringToNumber,
    getStartDate,
    isDateInvalid,
    isDateOutsideRange,
    isFromToValid
} from "../../../../utilities/dateTimeUtils";
import {
    dateInvalidError,
    dateOutsideRangeError,
    isMaxBaseCurrenciesReached,
    isNothingSelected,
    maxSelectionError,
    noSelectionError
} from "../helpers/productDetailsHelper";
import { AutocompleteWithCheckboxes } from "../../../components/FormComponents/AutocompleteWithCheckboxes";
import { formatPrice } from "../../../../utilities/PriceUtils";

const KAIKO_MAX_BASE_CURRENCIES = 100;

export interface IKaikoItem {
    id: string;
    code: string;
    name: string;
}

export interface IHistoricalBoxProps extends WithStyles<typeof styles> {
    product: string;
    firstAvailableDate: string;
    lastAvailableDate: string;
    onCheckout: any;
}

const KaikoHistoricalBoxPure: React.FunctionComponent<IHistoricalBoxProps> = ({
    product,
    firstAvailableDate,
    lastAvailableDate,
    onCheckout,
    classes
}: IHistoricalBoxProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const [selectedExchanges, setSelectedExchanges] = useState<IKaikoItem[]>([]);
    const [selectedBaseCurrencies, setSelectedBaseCurrencies] = useState<IKaikoItem[]>([]);
    const [from, setFrom] = useState<string>(getStartDate(lastAvailableDate));
    const [to, setTo] = useState<string>(lastAvailableDate);

    const kaikoPricingPackages = useSelector(state => state.kaiko?.packages);
    const allKaikoExchanges = useSelector(state => state.kaiko?.levelOne);
    const allKaikoBaseCurrencies = useSelector(state => state.kaiko?.levelTwo);
    const kaikoFilesInfo = useSelector(state => state.kaiko?.filesInfo);

    useEffect(() => {
        dispatch(getPricingPackages(product));
        if (isFromToValid(from, to)) {
            const params = {
                from: convertDateStringToNumber(from),
                to: convertDateStringToNumber(to)
            };
            dispatch(getExchanges(product, params));
        }
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (
            !isNothingSelected(selectedBaseCurrencies.length) &&
            !isMaxBaseCurrenciesReached(selectedBaseCurrencies.length, KAIKO_MAX_BASE_CURRENCIES)
        ) {
            const params = {
                levelOne: selectedExchanges.map(i => i.code)?.join(","),
                levelTwo: selectedBaseCurrencies.map(i => i.code)?.join(","),
                from: convertDateStringToNumber(from),
                to: convertDateStringToNumber(to)
            };
            dispatch(getKaikoFilesInfo(product, params));
        }
        // eslint-disable-next-line
    }, [selectedBaseCurrencies]);

    const handleDateChange = (from, to) => {
        setFrom(from);
        setTo(to);

        const params = {
            from: convertDateStringToNumber(from),
            to: convertDateStringToNumber(to)
        };

        if (isFromToValid(from, to)) {
            dispatch(getExchanges(product, params));
        }
        setSelectedExchanges([]);
        setSelectedBaseCurrencies([]);
    };

    const handleExchangeChange = (exchanges: IKaikoItem[]) => {
        setSelectedExchanges(exchanges);
        const params = {
            levelOne: exchanges.map(i => i.code)?.join(","),
            from: convertDateStringToNumber(from),
            to: convertDateStringToNumber(to)
        };
        if (exchanges.length !== 0) dispatch(getBaseCurrencies(product, params));
        setSelectedBaseCurrencies([]);
    };

    const handleBaseCurrenciesChange = (baseCurrencies: IKaikoItem[]) => {
        setSelectedBaseCurrencies(baseCurrencies);
    };

    const handleCheckout = () => {
        const additionalCheckoutParameters = {
            filterableProductParameters: {
                levelOne: selectedExchanges,
                levelTwo: selectedBaseCurrencies
            }
        };
        onCheckout(from, to, kaikoFilesInfo?.totalPrice, additionalCheckoutParameters);
    };

    return (
        <>
            <Grid item xs={12} key="historical-box-kaiko">
                <Paper elevation={0} className={classes.paper}>
                    <Box margin={4} className={classes.kaikoCard}>
                        <Typography variant="h2">One-off order</Typography>
                        <FormControl style={{ width: "100%" }}>
                            <div className={classes.sectionInput}>
                                <Grid container spacing={2}>
                                    <Grid item xs={4}>
                                        <Typography variant={"caption"}>Date from</Typography>
                                        <FormDateField
                                            disabled={false}
                                            placeholder="From"
                                            value={from}
                                            firstAvailableDate={firstAvailableDate}
                                            lastAvailableDate={lastAvailableDate}
                                            onChange={value => handleDateChange(value, to)}
                                        />
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography variant={"caption"}>Date to</Typography>
                                        <FormDateField
                                            disabled={false}
                                            placeholder="To"
                                            value={to}
                                            firstAvailableDate={firstAvailableDate}
                                            lastAvailableDate={lastAvailableDate}
                                            onChange={value => handleDateChange(from, value)}
                                        />
                                    </Grid>
                                </Grid>

                                <Grid container spacing={10}>
                                    <Grid item xs={8}>
                                        <Typography variant={"caption"}>Select Exchanges</Typography>
                                    </Grid>
                                </Grid>

                                <AutocompleteWithCheckboxes
                                    id="kaiko-exchanges"
                                    allItems={allKaikoExchanges || []}
                                    selectedItems={selectedExchanges}
                                    onChange={newValue => handleExchangeChange(newValue)}
                                ></AutocompleteWithCheckboxes>

                                <Grid container spacing={10} className={classes.marginTop}>
                                    <Grid item xs={8}>
                                        <Typography variant={"caption"}>Select Assets</Typography>
                                    </Grid>
                                </Grid>

                                <AutocompleteWithCheckboxes
                                    id="kaiko-base-currencies"
                                    allItems={allKaikoBaseCurrencies || []}
                                    selectedItems={selectedBaseCurrencies}
                                    onChange={newValue => handleBaseCurrenciesChange(newValue)}
                                    disabled={selectedExchanges.length === 0}
                                ></AutocompleteWithCheckboxes>

                                <KaikoDetailsComponent
                                    dates={[from, to]}
                                    exchanges={selectedExchanges}
                                    currencies={selectedBaseCurrencies}
                                />
                            </div>
                            {selectedBaseCurrencies.length !== 0 && kaikoFilesInfo && kaikoFilesInfo?.isAnyUnavailable
                                ? <>
                                    <Typography>
                                        Not all exchange/asset combinations are available for the chosen date range.
                                    </Typography>
                                </>
                                : null
                            }
                        </FormControl>
                        <div className={classes.buttonCheckout}>
                            {selectedBaseCurrencies.length > 0 && kaikoFilesInfo?.package && (
                                <div className={classes.pricingDetailsSection}>
                                    <Grid container spacing={2} className={classes.marginTop}>
                                        <Grid item xs={7}>
                                            <Typography className={classes.titleName} variant="body2">
                                                Package
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Typography className={classes.price} variant="body2">
                                                <b>{kaikoFilesInfo?.package?.name}</b>
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={7}>
                                            <Typography className={classes.titleName} variant="body2">
                                                Total price
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Typography className={classes.price} variant="body2">
                                                <b>{formatPrice(kaikoFilesInfo?.totalPrice)}</b>
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={1} className={classes.alignRight}>
                                            <Tooltip title="Price details">
                                                <span className={classes.price}>
                                                    <KaikoPricingInfoDialog packages={kaikoPricingPackages} />
                                                </span>
                                            </Tooltip>
                                        </Grid>
                                    </Grid>
                                </div>
                            )}
                            <BlueButton
                                disabled={
                                    isDateInvalid(from, to) ||
                                    isNothingSelected(selectedBaseCurrencies.length) ||
                                    isMaxBaseCurrenciesReached(
                                        selectedBaseCurrencies.length,
                                        KAIKO_MAX_BASE_CURRENCIES
                                    ) ||
                                    isDateOutsideRange(from, to, firstAvailableDate, lastAvailableDate)
                                }
                                fullWidth
                                onClick={handleCheckout}
                            >
                                {t("dataView.addToCart")}
                            </BlueButton>
                            {/* {remark !== "" ? <FormHelperText error={true}>{remark}</FormHelperText> : null} */}
                            {dateInvalidError(from, to) ??
                                noSelectionError(selectedBaseCurrencies.length) ??
                                maxSelectionError(selectedBaseCurrencies.length, KAIKO_MAX_BASE_CURRENCIES) ??
                                dateOutsideRangeError(from, to, firstAvailableDate, lastAvailableDate)}
                        </div>
                    </Box>
                </Paper>
            </Grid>
        </>
    );
};

export const KaikoProductDetailsHistoricalBox = withStyles(styles)(KaikoHistoricalBoxPure);
